import { COMPANY_TYPE_ID, CONTACT_TYPE_ID, DEAL_TYPE_ID, LEAD_TYPE_ID, PRODUCT_TYPE_ID, TASK_TYPE_ID } from 'customer-data-objects/constants/ObjectTypeIds';
import { getObjectLabel } from 'customer-data-objects/objects/getObjectLabel';
import FormattedPropertyV2 from 'customer-data-properties/formatted/FormattedPropertyV2';
import { ProductBundleIcon } from 'foundations-assets/icons/components/ProductBundleIcon';
import { Flex } from 'foundations-components/components/Flex';
import { View } from 'foundations-components/components/View';
import { IconHolder } from 'foundations-components/transitional/components/IconHolder';
import { Link } from 'foundations-components/transitional/components/Link';
import { TableHoverCell } from 'foundations-components/transitional/components/Table';
import { gate } from 'hub-http/gates';
import { IconButton } from 'foundations-components/transitional/components/IconButton';
import { RightIcon } from 'foundations-assets/icons/components/RightIcon';
import { DownIcon } from 'foundations-assets/icons/components/DownIcon';
import { alwaysUndefined } from '../../utils/alwaysUndefined';
import { AvatarCellWrapper } from '../AvatarCell';
import EmptyState from '../EmptyState';
import TruncateDiv from '../TruncateDiv';
import { useLinkProps } from '../getLinkDetails';
import { CRM_PROPERTY_DATE_FORMAT, CRM_PROPERTY_DATE_TIME_FORMAT } from '../properties/constants';
import { DealMeetingFollowUpIcon } from './DealMeetingFollowUpIcon';
import { TaskCompleteIcon } from './TaskCompleteIcon';
import emptyFunction from 'react-utils/emptyFunction';
import { useAuth } from '../../auth/context/AuthContext';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const SDP_GATE = gate('DealAI:SDPNotifications'); // Temporary gate name, it will change before release
export const TASK_INDEX_GATE = gate('Tasks:CrmPlatform');
const AVATAR_OBJECT_TYPES = [CONTACT_TYPE_ID, COMPANY_TYPE_ID, LEAD_TYPE_ID];
const MaybeAvatarWrapper = ({
  value,
  objectTypeId,
  objectId,
  object,
  children
}) => {
  if (!AVATAR_OBJECT_TYPES.includes(objectTypeId)) {
    return children;
  }
  return /*#__PURE__*/_jsx(AvatarCellWrapper, {
    objectTypeId: objectTypeId,
    displayName: value,
    id: objectId,
    object: object,
    children: children
  });
};
const getIcons = (objectTypeId, getSecondaryPropertyValue) => {
  if (objectTypeId === PRODUCT_TYPE_ID && getSecondaryPropertyValue('hs_bundle_type') === 'open') {
    return /*#__PURE__*/_jsx(IconHolder, {
      icon: ProductBundleIcon,
      compact: true,
      "data-test-id": "label-cell-product-bundle-icon"
    });
  }
  return null;
};
const NoLinkIfDisabled = props => {
  if (props.disabled) {
    return props.children;
  }
  return /*#__PURE__*/_jsx(Link, Object.assign({}, props));
};
const ExpandButton = ({
  config
}) => {
  if (!config) {
    return null;
  }
  return /*#__PURE__*/_jsx(IconButton, {
    onClick: config.onExpand,
    size: "sm",
    use: "unstyled",
    "aria-expanded": config.isExpanded,
    "aria-controls": config.ariaControlsId,
    "aria-label": config.isExpanded ? config.ariaLabelExpanded : config.ariaLabelCollapsed,
    children: config.isExpanded ? /*#__PURE__*/_jsx(DownIcon, {}) : /*#__PURE__*/_jsx(RightIcon, {})
  });
};
const DisplayValue = ({
  objectTypeId,
  objectId,
  value,
  propertyDefinition,
  getSecondaryPropertyValue,
  onPropertyUpdatesSucceeded,
  onPropertyUpdatesFailed
}) => {
  const {
    hasGate
  } = useAuth();
  const shouldShowTaskCompleteIcon = objectTypeId === TASK_TYPE_ID && hasGate(TASK_INDEX_GATE);
  const shouldShowDealMeetingFollowUpIcon = objectTypeId === DEAL_TYPE_ID && hasGate(SDP_GATE);
  const icons = getIcons(objectTypeId, getSecondaryPropertyValue);
  if (objectTypeId === CONTACT_TYPE_ID) {
    return /*#__PURE__*/_jsx(TruncateDiv, {
      children: /*#__PURE__*/_jsx("span", {
        "data-test-id": `label-cell-unformatted-property-${objectTypeId}-${objectId}-${propertyDefinition.name}`,
        children: value || /*#__PURE__*/_jsx(EmptyState, {})
      })
    });
  }
  return /*#__PURE__*/_jsxs(Flex, {
    flexDirection: "row",
    alignItems: "center",
    gap: shouldShowTaskCompleteIcon ? '200' : '100',
    children: [shouldShowTaskCompleteIcon && /*#__PURE__*/_jsx(TaskCompleteIcon, {
      objectTypeId: objectTypeId,
      objectId: objectId,
      getSecondaryPropertyValue: getSecondaryPropertyValue,
      onSuccess: onPropertyUpdatesSucceeded,
      onFailure: onPropertyUpdatesFailed
    }), shouldShowDealMeetingFollowUpIcon && /*#__PURE__*/_jsx(DealMeetingFollowUpIcon, {
      objectId: objectId,
      objectTypeId: objectTypeId,
      getSecondaryPropertyValue: getSecondaryPropertyValue
    }), /*#__PURE__*/_jsx(TruncateDiv, {
      children: /*#__PURE__*/_jsx(FormattedPropertyV2, {
        "data-test-id": `label-cell-formatted-property-${objectTypeId}-${objectId}-${propertyDefinition.name}`,
        objectTypeId: objectTypeId,
        objectId: objectId,
        property: propertyDefinition,
        value: value,
        getDraftValue: getSecondaryPropertyValue,
        getSavedValue: alwaysUndefined,
        dateFormat: CRM_PROPERTY_DATE_FORMAT,
        dateTimeFormat: CRM_PROPERTY_DATE_TIME_FORMAT,
        autoManageHighlySensitiveValue: false,
        enableAutolinking: false
      })
    }), icons]
  });
};
const LabelCellPresentational = ({
  requiredCellProps,
  expandConfig,
  hoverContent,
  objectTypeDefinition,
  object,
  getLinkDetails,
  getSecondaryPropertyValue,
  propertyDefinition,
  onPropertyUpdatesSucceeded = emptyFunction,
  onPropertyUpdatesFailed = emptyFunction
}) => {
  const {
    'data-onboarding': dataOnboarding,
    'data-table-external-id': dataTableExternalId,
    'data-test-id': dataTestId,
    'data-column-index': dataColumnIndex
  } = requiredCellProps;
  const linkProps = useLinkProps({
    getLinkDetails,
    objectTypeId: objectTypeDefinition.objectTypeId,
    objectId: object.objectId
  });

  // HACK: Technically this is a formatted name for contacts, but in all other
  // cases it is actually the value of primaryDisplayLabelPropertyName
  const value = getObjectLabel(objectTypeDefinition, object);
  const content = !propertyDefinition ?
  /*#__PURE__*/
  //propertyDefinition is required here, but this is a sane error fallback
  //CDP does this often - showing the internal value when an error occurs
  _jsx("span", {
    "data-test-id": "LabelCellPresentational-missing-property-definition",
    "data-objectTypeId": objectTypeDefinition.objectTypeId,
    "data-objectId": object.objectId,
    children: value || /*#__PURE__*/_jsx(EmptyState, {})
  }) : /*#__PURE__*/_jsxs(Flex, {
    flexDirection: "row",
    alignItems: "center",
    gap: "200",
    children: [expandConfig && /*#__PURE__*/_jsx(View, {
      flexShrink: "0",
      children: /*#__PURE__*/_jsx(ExpandButton, {
        config: expandConfig
      })
    }), /*#__PURE__*/_jsx(View, {
      flex: "1",
      minInlineSize: "0",
      children: /*#__PURE__*/_jsx(MaybeAvatarWrapper, {
        value: value,
        objectTypeId: objectTypeDefinition.objectTypeId,
        objectId: object.objectId,
        object: object,
        children: /*#__PURE__*/_jsx(NoLinkIfDisabled, Object.assign({}, linkProps, {
          children: /*#__PURE__*/_jsx(DisplayValue, {
            objectTypeId: objectTypeDefinition.objectTypeId,
            objectId: object.objectId,
            propertyDefinition: propertyDefinition,
            value: value,
            getSecondaryPropertyValue: getSecondaryPropertyValue,
            onPropertyUpdatesSucceeded: onPropertyUpdatesSucceeded,
            onPropertyUpdatesFailed: onPropertyUpdatesFailed
          })
        }))
      })
    })]
  });
  return /*#__PURE__*/_jsx(TableHoverCell, {
    as: 'td',
    "data-column-index": dataColumnIndex,
    "data-onboarding": dataOnboarding,
    "data-table-external-id": dataTableExternalId,
    "data-test-id": dataTestId,
    hoverContent: hoverContent,
    children: content
  });
};
export default LabelCellPresentational;